/**
 * ParentSignupWithInvitePage
 * 학부모 초대 링크 회원가입 페이지
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  UserGroupIcon,
  PhoneIcon,
  KeyIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import ParentAuthRepository from '../repositories/ParentAuthRepository';

const ParentSignupWithInvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // 초대 정보 상태
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteError, setInviteError] = useState('');
  const [inviteInfo, setInviteInfo] = useState<any>(null);

  // 폼 상태
  const [step, setStep] = useState(2); // 1: 카카오톡 인증, 2: 회원정보 입력 (임시로 2부터 시작)
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // UI 상태
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [error, setError] = useState('');

  // 초대 토큰 검증
  useEffect(() => {
    if (!token) {
      setInviteError('유효하지 않은 초대 링크입니다');
      setInviteLoading(false);
      return;
    }

    const verifyInvite = async () => {
      try {
        const result = await ParentAuthRepository.verifyInvite(token);
        setInviteInfo(result);
        setPhone(result.phone); // 초대받은 전화번호 자동 입력
        setInviteValid(true);
      } catch (err: any) {
        setInviteError(err.message || '초대 링크 검증에 실패했습니다');
      } finally {
        setInviteLoading(false);
      }
    };

    verifyInvite();
  }, [token]);

  // SMS 인증 코드 발송
  const handleSendSMS = async () => {
    if (!phone.trim()) {
      setError('전화번호를 입력해주세요');
      return;
    }

    setSmsLoading(true);
    setError('');

    try {
      await ParentAuthRepository.sendSmsCode({ phone });
      setSmsSent(true);
      alert('인증번호가 발송되었습니다');
    } catch (err: any) {
      // 에러 발생해도 인증번호 입력란은 표시 (테스트용)
      setSmsSent(true);
      setError(err.message || '인증번호 발송에 실패했습니다. 테스트용 인증번호를 입력하세요.');
    } finally {
      setSmsLoading(false);
    }
  };

  // SMS 인증 코드 검증
  const handleVerifySMS = async () => {
    if (!smsCode.trim()) {
      setError('인증번호를 입력해주세요');
      return;
    }

    setSmsLoading(true);
    setError('');

    try {
      const result = await ParentAuthRepository.verifySmsCode({
        phone,
        code: smsCode,
      });

      if (result.valid) {
        setSmsVerified(true);
        setStep(2); // 다음 단계로
      } else {
        setError('인증번호가 올바르지 않습니다');
      }
    } catch (err: any) {
      setError(err.message || '인증에 실패했습니다');
    } finally {
      setSmsLoading(false);
    }
  };

  // 회원가입 완료
  const handleSignup = async () => {
    if (!name.trim()) {
      setError('이름을 입력해주세요');
      return;
    }

    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다');
      return;
    }

    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다');
      return;
    }

    if (!token) {
      setError('초대 토큰이 없습니다');
      return;
    }

    setSignupLoading(true);
    setError('');

    try {
      const result = await ParentAuthRepository.signupWithInvite({
        token,
        name: name.trim(),
        phone,
        sms_code: smsCode || '000000', // SMS 인증 비활성화 상태이므로 더미값
        password,
        device_info: {
          user_agent: navigator.userAgent,
        },
      });

      // Supabase 세션은 이미 onAuthStateChange에서 처리됨
      // setSession 호출하지 않음 - 무한 대기 방지

      // 학부모 정보 저장 (로컬 캐시용)
      if (result.parent) {
        localStorage.setItem('parentInfo', JSON.stringify(result.parent));
      }

      // 가입 완료 메시지
      alert(`회원가입이 완료되었습니다! ${inviteInfo.student_name} 학생과 연결되었습니다.`);

      // 학부모 대시보드로 이동
      navigate('/parent/dashboard');
    } catch (err: any) {
      console.error('회원가입 에러:', err);
      setError(err.message || '회원가입에 실패했습니다');
    } finally {
      setSignupLoading(false);
    }
  };

  // 로딩 중
  if (inviteLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">초대 링크 확인 중...</p>
        </div>
      </div>
    );
  }

  // 초대 링크 오류
  if (!inviteValid || inviteError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <ExclamationCircleIcon className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              초대 링크 오류
            </h1>
            <p className="text-gray-600">{inviteError}</p>
          </div>
          <button
            onClick={() => navigate('/parent/login')}
            className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 font-medium"
          >
            로그인 페이지로 이동
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        {/* 헤더 */}
        <div className="text-center mb-6">
          <UserGroupIcon className="h-16 w-16 text-purple-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            학부모 회원가입
          </h1>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-700">
              <strong className="text-purple-700">{inviteInfo.student_name}</strong> 학생의 학부모로 가입합니다
            </p>
            <p className="text-xs text-gray-600 mt-1">
              독서실: {inviteInfo.organization_name}
            </p>
          </div>
        </div>

        {/* Step 1: 카카오톡 인증 */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                전화번호
              </label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="010-1234-5678"
                  disabled={smsSent}
                />
                <button
                  onClick={handleSendSMS}
                  disabled={smsLoading || smsSent}
                  className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {smsSent ? '발송됨' : '인증번호'}
                </button>
              </div>
            </div>

            {smsSent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  인증번호 (6자리)
                </label>
                <input
                  type="text"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value)}
                  maxLength={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="123456"
                />
              </div>
            )}

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleVerifySMS}
              disabled={!smsSent || !smsCode.trim() || smsLoading}
              className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {smsLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  인증 중...
                </>
              ) : (
                <>
                  <PhoneIcon className="h-5 w-5" />
                  인증 확인
                </>
              )}
            </button>
          </div>
        )}

        {/* Step 2: 회원정보 입력 */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="text-sm text-green-700">전화번호 인증 완료</span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                이름 *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="홍길동"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                비밀번호 * (6자 이상)
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="6자 이상 입력"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                비밀번호 확인 *
              </label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="비밀번호 재입력"
              />
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleSignup}
              disabled={signupLoading}
              className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {signupLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  가입 중...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5" />
                  회원가입 완료
                </>
              )}
            </button>
          </div>
        )}

        {/* 진행 표시 */}
        <div className="mt-6 flex items-center justify-center gap-2">
          <div
            className={`h-2 w-2 rounded-full ${
              step >= 1 ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          />
          <div
            className={`h-2 w-2 rounded-full ${
              step >= 2 ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default ParentSignupWithInvitePage;
